import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { Text } from '../../common';
import { FormGroup } from '../../components/form';
import maverickPortalhttp from '../../utils/webService/webService';
import maverickPortalApiEndPoint from '../../config/apiEndPoint';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import mixPanel from '../../utils/helpers/mixPanel';
import { validateWebLinkUrl } from '../../utils/helpers/urlValidator';
import InvalidProjectScreen from './InvalidProjectScreen';
import ProjectClosedScreen from './ProjectClosedScreen';
import ErrorScreen1 from '../../assets/images/ErrorScreen1.png';
import explorastoryPromoVideo from '../../assets/images/Explorastory_promo_video.mp4';

const LoginPage = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background: #ffffff;
  display: flex;
  justify-content: center;
  align-items: stretch;
  padding-top: calc(24px + env(safe-area-inset-top, 0px));
  padding-right: calc(20px + env(safe-area-inset-right, 0px));
  padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  padding-left: calc(20px + env(safe-area-inset-left, 0px));
  overflow: auto;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;

  @media (max-height: 740px) {
    padding-top: calc(12px + env(safe-area-inset-top, 0px));
    padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  }
`;

const LoginContent = styled.div`
  width: min(390px, 100%);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 100%;
  box-sizing: border-box;
`;

const Logo = styled.img`
  width: 168px;
  height: auto;
  display: block;
  margin: 12px auto 36px;

  @media (max-height: 740px) {
    width: 132px;
    margin: 4px auto 16px;
  }
`;

const HeroTitle = styled.h1`
  margin: 0 0 12px;
  font-size: 22px;
  line-height: 1.3;
  font-weight: 700;
  color: #333333;
  text-align: center;
`;

const HeroSubtitle = styled.p`
  margin: 0 0 36px;
  font-size: 15px;
  line-height: 1.45;
  font-weight: 400;
  color: #777777;
  text-align: center;

  @media (max-height: 740px) {
    margin-bottom: 16px;
  }
`;

const FieldLabel = styled.label`
  display: block;
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
  color: #333333;
  text-align: left;
`;

const PhoneField = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid #d8d8d8;
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
  min-height: 52px;
  box-sizing: border-box;

  &:focus-within {
    border-color: #b7b7b7;
  }
`;

const CountryCodeSelect = styled.select`
  border: none;
  background: transparent;
  font-size: 15px;
  font-weight: 700;
  color: #333333;
  cursor: pointer;
  padding: 14px 10px 14px 14px;
  min-width: 64px;
  flex-shrink: 0;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const PhoneDivider = styled.span`
  width: 1px;
  align-self: stretch;
  background: #e0e0e0;
  margin: 10px 0;
  flex-shrink: 0;
`;

const MobileInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  /* iOS Safari zooms focused inputs under 16px and often stays zoomed after blur */
  font-size: 16px;
  font-weight: 500;
  color: #333333;
  outline: none;
  padding: 14px 14px 14px 12px;
  min-width: 0;

  &::placeholder {
    color: #b0b0b0;
    font-weight: 400;
  }

  &:focus {
    outline: none;
  }
`;

const HelperText = styled.p`
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.4;
  color: #888888;
  text-align: left;
`;

const TermsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 18px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: #666666;
  text-align: left;
`;

const TermsCheckbox = styled.input`
  width: 15px;
  height: 15px;
  margin-top: 1px;
  flex-shrink: 0;
  cursor: pointer;
`;

const TermsLink = styled.a`
  color: #4a90c8;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SendOtpButton = styled.button`
  display: block;
  width: 100%;
  flex-shrink: 0;
  margin: 22px 0 0;
  padding: 15px 16px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  color: #2f2f2f;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  background: ${props => (props.disabled ? '#d9d9d9' : '#9ebe21')};
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.94;
  }
`;

const Spacer = styled.div`
  flex: 1;
  min-height: 16px;
`;

const VideoCard = styled.button`
  width: 100%;
  margin-top: 12px;
  flex-shrink: 0;
  padding: 14px 16px;
  border: 1px solid #ecece6;
  border-radius: 12px;
  background: #f6f6f0;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f1f1ea;
  }
`;

const PlayBadge = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #9ebe21;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    margin-left: 2px;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 10px solid #1f1f1f;
  }
`;

const VideoCardLabel = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #333333;
`;

const ErrorText = styled(Text)`
  color: #f44336;
  font-size: 12px;
  margin-top: 8px;
  text-align: left;
  width: 100%;
  display: block;
`;

const PopupOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const PopupContent = styled.div`
  width: min(520px, 100%);
  background: #ffffff;
  border-radius: 12px;
  padding: 16px;
  position: relative;
`;

const CloseVideoButton = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: #666;
  cursor: pointer;
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
`;

const Loader = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #9ebe21;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoaderMessage = styled(Text)`
  font-size: 16px;
  color: #333;
  font-weight: 500;
`;

// Country code to flag emoji mapping
const countryFlagMap = {
  'IN': '🇮🇳', 'US': '🇺🇸', 'CA': '🇨🇦', 'AE': '🇦🇪', 'MA': '🇲🇦',
  'BH': '🇧🇭', 'CY': '🇨🇾', 'EG': '🇪🇬', 'IR': '🇮🇷', 'IQ': '🇮🇶',
  'TL': '🇹🇱', 'JO': '🇯🇴', 'KW': '🇰🇼', 'LB': '🇱🇧', 'OM': '🇴🇲',
  'PS': '🇵🇸', 'QA': '🇶🇦', 'SA': '🇸🇦', 'SY': '🇸🇾', 'TR': '🇹🇷',
  'YE': '🇾🇪', 'DZ': '🇩🇿', 'LY': '🇱🇾', 'SD': '🇸🇩', 'TN': '🇹🇳',
  'MR': '🇲🇷'
};

function LoginRespondent() {
  const navigate = useNavigate(); // Initialize useNavigate
  mixPanel.init();
  const [searchParams] = useSearchParams();
  const getResponseSummary = useCallback((resp) => {
    if (!resp) return null;
    const source = resp.response ?? resp;
    if (!source || typeof source !== 'object') return null;
    const { config, data, status } = source;
    const summary = {};
    if (typeof config !== 'undefined') summary.config = config;
    if (typeof data !== 'undefined') summary.data = data;
    if (typeof status !== 'undefined') summary.status = status;
    return Object.keys(summary).length ? summary : null;
  }, []);
  const [mobileNumber, setMobileNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91'); // State holds the dial code like '+1'
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false); // <-- loading state
  const [projectId, setProjectId] = useState('');
  const [token1, setToken1] = useState('');
  const [projectSlug, setProjectSlug] = useState(''); // Add state for projectSlug
  const [qRProject, setQRProject] = useState(''); // Add state for qRProject
  const [showInvalidScreen, setShowInvalidScreen] = useState(false);
  const [showProjectClosedScreen, setShowProjectClosedScreen] = useState(false);
  const [projectClosedVariant, setProjectClosedVariant] = useState('ended'); // 'ended' | 'full'
  const [isValidatingWeblink, setIsValidatingWeblink] = useState(true);
  const [isGeneralized, setIsGeneralized] = useState(() => {
      const currentUrl = window.location.href;
      return !currentUrl.includes('&');
    });
  const [otpSent, setOtpSent] = useState(false); // Track if OTP has been sent
  const [countryCodes, setCountryCodes] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [countryPhoneSizeMap, setCountryPhoneSizeMap] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showHelpVideo, setShowHelpVideo] = useState(false);

  const maxPhoneLength = countryPhoneSizeMap[countryCode] || 10;
  const isOtpReady =
    termsAccepted &&
    mobileNumber.length === maxPhoneLength &&
    !loading &&
    !loadingCountries;

  useEffect(() => {
    if (!error) return;
    const timeoutId = setTimeout(() => {
      setError('');
    }, 5000);
    return () => clearTimeout(timeoutId);
  }, [error]);

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    const maxLength = countryPhoneSizeMap[countryCode] || 10;
    if (value.length <= maxLength) {
      setMobileNumber(value);
      if (error) setError('');
    }
  };

  const handleMobileKeyDown = (e) => {
    const key = e.key;
    const allowed = /[0-9]/.test(key) || ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key);
    if (!allowed && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }
  };

  const handleMobilePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData('text') || '').replace(/[^0-9]/g, '');
    const maxLength = countryPhoneSizeMap[countryCode] || 10;
    setMobileNumber(pasted.slice(0, maxLength));
  };

  useEffect(() => {
    const validateWeblinkAndSetParams = async () => {
      // First validate URL format
      const urlValidation = validateWebLinkUrl();
      if (!urlValidation.isValid) {
        console.error('URL validation failed:', urlValidation.error);
        setShowInvalidScreen(true);
        setIsValidatingWeblink(false);
        return;
      }

      // Use validated identificationID from URL validation
      const validatedIdentificationID = urlValidation.identificationID;
      const validatedProjectIdentity = urlValidation.projectname || urlValidation.projectId;
      
      if (!validatedIdentificationID) {
        console.error('No URL parameter found after ? mark');
        setShowInvalidScreen(true);
        setIsValidatingWeblink(false);
        return;
      }

      // Call weblinkValidation API to check if link is valid
      try {
        setIsValidatingWeblink(true);
        const payload = {
          identificationID: validatedIdentificationID,
          projectIdentity: validatedProjectIdentity
        };

        const response = await maverickPortalhttp.makeRequest(
          maverickPortalApiEndPoint.weblinkValidation.url,
          maverickPortalApiEndPoint.weblinkValidation.methodType,
          payload
        );

        if (response && response.data.Status && response.data.Status.StatusCode === "-1") {
          if (response.data.Response) {
            const projectStatusText = String(response.data.Response.projectStatus ?? '').toLowerCase();
            const projectEndVal = response.data.Response.projectEnd;
            const accessLoginVal = response.data.Response.accessLogin;

            const isProjectEndZero = projectEndVal === 0 || projectEndVal === '0';
            const isProjectFull =
              projectStatusText.includes('full') ||
              projectStatusText.includes('quota') ||
              projectStatusText.includes('maximum') ||
              projectStatusText.includes('max');
            const isAccessLoginBlocked = accessLoginVal === 0 || accessLoginVal === '0';

            const shouldShowProjectClosedScreen = isProjectEndZero || isProjectFull || isAccessLoginBlocked;
            if (shouldShowProjectClosedScreen) {
              setProjectClosedVariant(isProjectFull || isAccessLoginBlocked ? 'full' : 'ended');
              setShowProjectClosedScreen(true);
              setShowInvalidScreen(false);
              setIsValidatingWeblink(false);
              return;
            }
            
            // Check for invalid project link
            if (response.data.Response.isAvaliableProjectFlag === 0) {
              setShowInvalidScreen(true);
              setIsValidatingWeblink(false);
              return;
            }
          }
        }

        // If we reach here, the link is valid according to API
        setIsValidatingWeblink(false);

        const projectId = searchParams.get('Project_ID');
        const token1 = searchParams.get('Token');
        const projectSlug = searchParams.get('projectSlug');
        const qRProject = searchParams.get('qRProject');

        setProjectId(projectId);
        setToken1(token1);
        setProjectSlug(projectSlug);
        setQRProject(qRProject);
      } catch (error) {
        console.error('Error validating weblink:', error);
        // On API error, show invalid screen to be safe
        setShowInvalidScreen(true);
        setIsValidatingWeblink(false);
      }
    };

    validateWeblinkAndSetParams();
  }, [searchParams]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoadingCountries(true);
        const response = await maverickPortalhttp.makeRequest(
          maverickPortalApiEndPoint.getCountryDetails.url,
          maverickPortalApiEndPoint.getCountryDetails.methodType
        );

        if (response.data?.Status?.StatusCode === '1' && response.data?.Response?.selectCountry) {
          const countriesData = response.data.Response.selectCountry;

          const formattedCountries = countriesData.map(country => {
            let countryCodeStr = null;
            if (country.phoneCode) {
              countryCodeStr = `+${country.phoneCode}`;
            }

            const phoneSize = country.phoneSize || 10;

            return {
              code: countryCodeStr,
              country: country.code,
              flag: countryFlagMap[country.code] || '',
              phoneSize: phoneSize
            };
          }).filter(country => country.code);

          if (formattedCountries.length > 0) {
            setCountryCodes(formattedCountries);
            const phoneSizeMap = {};
            formattedCountries.forEach(country => {
              phoneSizeMap[country.code] = country.phoneSize;
            });
            setCountryPhoneSizeMap(phoneSizeMap);

            const defaultCountry = formattedCountries.find(c => c.code === '+91') || formattedCountries[0];
            if (defaultCountry) {
              setCountryCode(defaultCountry.code);
            }
          } else {
            const fallbackCountries = [
              { code: '+1', country: 'US', flag: '🇺🇸', phoneSize: 10 },
              { code: '+91', country: 'IN', flag: '🇮🇳', phoneSize: 10 },
              { code: '+1', country: 'CA', flag: '🇨🇦', phoneSize: 10 },
              { code: '+971', country: 'AE', flag: '🇦🇪', phoneSize: 9 },
              { code: '+212', country: 'MA', flag: '🇲🇦', phoneSize: 9 }
            ];
            setCountryCodes(fallbackCountries);
            const fallbackPhoneSizeMap = {
              '+1': 10,
              '+91': 10,
              '+971': 9,
              '+212': 9
            };
            setCountryPhoneSizeMap(fallbackPhoneSizeMap);
          }
        } else {
          const fallbackCountries = [
            { code: '+1', country: 'US', flag: '🇺🇸', phoneSize: 10 },
            { code: '+91', country: 'IN', flag: '🇮🇳', phoneSize: 10 },
            { code: '+1', country: 'CA', flag: '🇨🇦', phoneSize: 10 },
            { code: '+971', country: 'AE', flag: '🇦🇪', phoneSize: 9 },
            { code: '+212', country: 'MA', flag: '🇲🇦', phoneSize: 9 }
          ];
          setCountryCodes(fallbackCountries);
          const fallbackPhoneSizeMap = {
            '+1': 10,
            '+91': 10,
            '+971': 9,
            '+212': 9
          };
          setCountryPhoneSizeMap(fallbackPhoneSizeMap);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
        const fallbackCountries = [
          { code: '+1', country: 'US', flag: '🇺🇸', phoneSize: 10 },
          { code: '+91', country: 'IN', flag: '🇮🇳', phoneSize: 10 },
          { code: '+1', country: 'CA', flag: '🇨🇦', phoneSize: 10 },
          { code: '+971', country: 'AE', flag: '🇦🇪', phoneSize: 9 },
          { code: '+212', country: 'MA', flag: '🇲🇦', phoneSize: 9 }
        ];
        setCountryCodes(fallbackCountries);
        const fallbackPhoneSizeMap = {
          '+1': 10,
          '+91': 10,
          '+971': 9,
          '+212': 9
        };
        setCountryPhoneSizeMap(fallbackPhoneSizeMap);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []);

  // Clear any existing signup data when component mounts (e.g., when navigating back from OTP)
  useEffect(() => {
    // Clear signup data to ensure fresh start
    localStorage.removeItem('signupData');
    // Reset OTP sent state when component mounts
    setOtpSent(false);
  }, []);

  //User logged in check
  useEffect(() => {
    const project_name = localStorage.getItem('project_name');
    const gen_ID = localStorage.getItem('gen_ID');
    const isAuthenticated = checkAuth(); 

    if (isAuthenticated) {
      // Redirect away from login page if already logged in
      // Use the original token from URL params instead of gen_ID from localStorage
      if(isGeneralized){
        // For generalized URLs, use the token from URL params
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = urlParams.get(project_name);
        // Only use gen_ID as fallback if no token in URL
        const finalToken = tokenFromUrl || gen_ID;
        navigate(`/dashboard-resp?${project_name}=${finalToken}`, { replace: true });
      }else{
        // For personalized URLs, use the Token from URL params
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = urlParams.get('Token');
        // Only use gen_ID as fallback if no token in URL
        const finalToken = tokenFromUrl || gen_ID;
        navigate(`/dashboard-resp?Project_ID=${projectId}&Token=${finalToken}`, { replace: true });
      }
    }
  }, [projectId]);

  function checkAuth() {
    const token = localStorage.getItem('token');
    return !!token;
  }

  const handleCreateAccountNavigation = () => {
    const currentUrl = window.location.href;
    const projectName = currentUrl.split('?')[1]?.split('=')[0] || '';
    const token5 = currentUrl.split('=')[1] || '';
    if (!projectId) {
      navigate(`/sign-up?${projectName}=${token5}`);
    } else {
      navigate(`/sign-up?Project_ID=${projectId}&Token=${token1}`);
    }
  };

  const sendOtp = () => {
    setError('');
    setSuccessMessage('');
    setLoading(true);

    if (!mobileNumber) {
      setError('Enter Your Mobile Number.');
      setLoading(false);
      return;
    }

    if (!termsAccepted) {
      setError('Please accept the Terms and Conditions & Privacy Policy.');
      setLoading(false);
      return;
    }

    const maxLength = countryPhoneSizeMap[countryCode] || 10;
    if (mobileNumber.length !== maxLength) {
      setError(`Mobile number must be exactly ${maxLength} digits`);
      setLoading(false);
      return;
    }

    const selectedCountryObject = countryCodes.find(c => c.code === countryCode);
    if (!selectedCountryObject) {
      setError('Invalid country code selected.');
      setLoading(false);
      return;
    }

    const data = {
      phone: `${countryCode}${mobileNumber}`,
      Type: "Login",
      mobile: mobileNumber
    };

    maverickPortalhttp
      .makeRequest(
        maverickPortalApiEndPoint.twilioSendOTP2.url,
        maverickPortalApiEndPoint.twilioSendOTP2.methodType,
        data
      )
      .then((response) => {
        setLoading(false);
        if (response.data?.Status?.StatusCode == 1) {
          setSuccessMessage('OTP sent successfully!');
          setOtpSent(true); // Mark OTP as sent to enable forward navigation
          localStorage.setItem('signupData', JSON.stringify({
            mobile: mobileNumber,
            country_code: selectedCountryObject.country,
            dial_code: countryCode,
            phone: data.phone
          }));
            const currentUrl = window.location.href;
    const projectName = currentUrl.split('?')[1]?.split('=')[0] || '';
    const token5 = currentUrl.split('=')[1] || '';
          // Modified navigation logic based on projectSlug and qRProject
          if (!projectId) {
            // If qRProject exists and is a numerical value, use it
            // if (qRProject && !isNaN(qRProject)) {
            //   navigate(`/enter-otp?projectSlug=${qRProject}`, { replace: true });
            // } else {
            //   navigate(`/enter-otp?projectSlug=${projectSlug}`, { replace: true });
            // }
             navigate(`/enter-otp?${projectName}=${token5}`);
          } else {
            // Use original Project_ID and Token if projectSlug is not present
            navigate(`/enter-otp?Project_ID=${projectId}&Token=${token1}`);
          }
        } else {
          mixPanel.track('Log In Falied', {
            error: response?.data?.Status?.StatusDesc || 'Login OTP request failed',
            mobile: mobileNumber,
            phone: data.phone,
            errorCode: 441,
            errorDetails: getResponseSummary(response) || 'Unknown error',
            endpoint: maverickPortalApiEndPoint.twilioSendOTP.url,
            timestamp: new Date().toISOString(),
            request:data,
          });
          setError(response.data?.Status?.StatusDesc || 'Failed to send OTP');
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error('API Request Error:', err);
        
        const errorReason = err.response?.data?.Status?.StatusDesc || err.response?.data?.message || err.message || 'Unknown error';
        mixPanel.track('Log In Falied', {
          error: `error from login: ${errorReason}`,
          phone: data.phone,
          errorCode: 441,
          errorDetails: getResponseSummary(err),
          endpoint: maverickPortalApiEndPoint.twilioSendOTP.url,
          timestamp: new Date().toISOString(),
          request:data,
        });
        
        if (err.response) {
          console.error("Error response data:", err.response.data);
          setError(`Server error: ${err.response.status} - ${err.response.data?.message || 'Failed to send OTP'}`);
        } else if (err.request) {
          console.error("Error request:", err.request);
          setError('Network error. Please check your connection.');
        } else {
          console.error('Error message:', err.message);
          setError('Something went wrong. Please try again.');
        }
      });
  };

  // Show loading while validating weblink
  if (isValidatingWeblink) {
    return (
      <LoaderWrapper>
        <Loader />
        <LoaderMessage>Loading...</LoaderMessage>
      </LoaderWrapper>
    );
  }

  // Show invalid screen if URL is invalid or API validation failed
  if (showProjectClosedScreen) {
    return (
      <ProjectClosedScreen
        backgroundImageUrl={ErrorScreen1}
        title={projectClosedVariant === 'full' ? 'Project is Full' : undefined}
        description={
          projectClosedVariant === 'full'
            ? 'This project has reached its participant limit and is no longer accepting responses.'
            : undefined
        }
      />
    );
  }

  if (showInvalidScreen) {
    return <InvalidProjectScreen backgroundImageUrl={ErrorScreen1} />;
  }

  return (
    <LoginPage>
      <LoginContent>
        <Logo src={`${process.env.PUBLIC_URL}/logo.png`} alt="explorastory" />

        <HeroTitle>
          Share your experiences
          <br />
          with world&apos;s best brands
        </HeroTitle>
        <HeroSubtitle>New here or returning? Start with your mobile number.</HeroSubtitle>

        <FormGroup>
          <FieldLabel htmlFor="login-mobile-number">Mobile number</FieldLabel>
          <PhoneField>
            <CountryCodeSelect
              value={countryCode}
              disabled={loadingCountries}
              aria-label="Country code"
              onChange={e => {
                setCountryCode(e.target.value);
                setMobileNumber('');
                setError('');
              }}
            >
              {loadingCountries ? (
                <option value="">...</option>
              ) : (
                countryCodes.map(country => (
                  <option key={`${country.country}-${country.code}`} value={country.code}>
                    {country.code}
                  </option>
                ))
              )}
            </CountryCodeSelect>
            <PhoneDivider />
            <MobileInput
              id="login-mobile-number"
              key={countryCode}
              type="text"
              placeholder="Enter your mobile number"
              value={mobileNumber}
              inputMode="numeric"
              pattern="[0-9]*"
              onChange={handleMobileChange}
              onKeyDown={handleMobileKeyDown}
              onPaste={handleMobilePaste}
              maxLength={countryPhoneSizeMap[countryCode] || 10}
              showCharacters={false}
            />
          </PhoneField>
          <HelperText>We&apos;ll send a one-time code (OTP) to verify it&apos;s you.</HelperText>
          {error && <ErrorText>{error}</ErrorText>}
        </FormGroup>

        <TermsRow>
          <TermsCheckbox
            type="checkbox"
            checked={termsAccepted}
            onChange={e => {
              setTermsAccepted(e.target.checked);
              if (error) setError('');
            }}
          />
          <span>
            I&apos;ve read and agree to the{' '}
            <TermsLink
              href="https://www.humanifytechnologies.com/respondent-terms-and-condition"
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              Terms and Conditions
            </TermsLink>{' '}
            &amp;{' '}
            <TermsLink
              href="https://www.humanifytechnologies.com/respondent-privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              Privacy Policy
            </TermsLink>
          </span>
        </TermsRow>

        <SendOtpButton type="button" onClick={sendOtp} disabled={!isOtpReady}>
          {loading ? 'Sending...' : 'Send OTP'}
        </SendOtpButton>

        {successMessage && !error && (
          <Text color="green" style={{ marginTop: '12px', textAlign: 'center' }}>
            {successMessage}
          </Text>
        )}

        <Spacer />

        <VideoCard type="button" onClick={() => setShowHelpVideo(true)}>
          <PlayBadge aria-hidden="true" />
          <VideoCardLabel>Watch a 30 sec video</VideoCardLabel>
        </VideoCard>
      </LoginContent>

      {showHelpVideo && (
        <PopupOverlay onClick={() => setShowHelpVideo(false)}>
          <PopupContent onClick={e => e.stopPropagation()}>
            <CloseVideoButton type="button" onClick={() => setShowHelpVideo(false)} aria-label="Close video">
              ×
            </CloseVideoButton>
            <video
              src={explorastoryPromoVideo}
              playsInline
              autoPlay
              controls
              onEnded={() => setShowHelpVideo(false)}
              style={{
                width: '100%',
                maxHeight: '60vh',
                borderRadius: '8px',
                background: '#fff',
                display: 'block',
                objectFit: 'contain',
                marginTop: '20px',
              }}
            />
          </PopupContent>
        </PopupOverlay>
      )}
    </LoginPage>
  );
}

export default LoginRespondent;